class FacetFiltersDrawer extends HTMLElement {
  constructor() {
    super();
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onFiltersUpdated = this.onFiltersUpdated.bind(this);
    this.onFormChange = this.onFormChange.bind(this);
    this.bindTriggers = this.bindTriggers.bind(this);
    this._stickyInstance = null;
    this._closeTimer = null;
    this._onDrawerOpenEnd = this._onDrawerOpenEnd.bind(this);
  }

  connectedCallback() {
    this.portalDrawerToBody(true);
    this.allPanel = this.querySelector('.facets-desktop-drawer__panel--all');
    this.filterPanels = this.querySelectorAll('.facets-desktop-drawer__panel--filter');
    this.bindTriggers();
    this.bindApplyButtons();
    this.bindFormChange();
    this.bindSizeInputSync();
    this.captureAppliedFilterState();
    this.updateAllFiltersList();
    this.updateMobileChrome('all');
    this.fitColorLabels();
    this.initDesktopStickyUnit();
    this.fitFilterButtons = this.fitFilterButtons.bind(this);
    this.initFilterBarFit();
    this.syncStickyBarBackground();
    this.updateStickyButtonStates();
    document.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('facet-filters-updated', this.onFiltersUpdated);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('facet-filters-updated', this.onFiltersUpdated);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    this.teardownFilterBarFit();
    this.teardownDesktopStickyUnit();
  }

  onResize = () => {
    if (window.innerWidth < 1025) {
      this.teardownDesktopStickyUnit();
      this.teardownFilterBarFit();
    } else {
      this.initDesktopStickyUnit();
      this.initFilterBarFit();
      this.syncStickyBarBackground();
    }
    this.fitFilterButtons();
    const activeFilterPanel = this.querySelector('.facets-desktop-drawer__panel--filter.is-active');
    this.updateMobileChrome(activeFilterPanel ? 'filter' : 'all', activeFilterPanel?.id || null);
  };

  onScroll = () => {
    if (this._stickyInstance) {
      this.updateDesktopStickyUnit();
    }
  };

  portalDrawerToBody(force = false) {
    if (this.dataset.portaled === 'true' && !force) return;
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
    this.dataset.portaled = 'true';
  }

  initFilterBarFit() {
    if (window.innerWidth < 1025 || this._filterBarObserver) return;

    const stickyBar = document.querySelector('.facets-desktop-drawer__sticky-bar');
    if (!stickyBar) return;

    this._filterBarObserver = new ResizeObserver(() => this.fitFilterButtons());
    this._filterBarObserver.observe(stickyBar);
    requestAnimationFrame(() => this.fitFilterButtons());
  }

  teardownFilterBarFit() {
    this._filterBarObserver?.disconnect();
    this._filterBarObserver = null;
    document
      .querySelectorAll('.facets-desktop-drawer__filter-btn--overflow')
      .forEach((btn) => btn.classList.remove('facets-desktop-drawer__filter-btn--overflow'));
  }

  fitFilterButtons() {
    if (window.innerWidth < 1025) return;

    const bar = document.querySelector('.facets-desktop-drawer__sticky-bar');
    const buttonsWrap = bar?.querySelector('.facets-desktop-drawer__buttons');
    if (!bar || !buttonsWrap) return;

    const buttons = [...buttonsWrap.querySelectorAll('.facets-desktop-drawer__filter-btn')];
    buttons.forEach((btn) => btn.classList.remove('facets-desktop-drawer__filter-btn--overflow'));

    const barStyles = getComputedStyle(bar);
    const barGap = parseFloat(barStyles.columnGap || barStyles.gap) || 14;
    const barPadding =
      (parseFloat(barStyles.paddingLeft) || 0) + (parseFloat(barStyles.paddingRight) || 0);

    let reserved = barPadding;
    [...bar.children].forEach((child) => {
      if (child === buttonsWrap) return;
      reserved += child.offsetWidth + barGap;
    });

    const available = Math.max(bar.clientWidth - reserved, 0);
    buttonsWrap.style.maxWidth = `${available}px`;

    const wrapStyles = getComputedStyle(buttonsWrap);
    const btnGap = parseFloat(wrapStyles.columnGap || wrapStyles.gap) || 10;

    let hidden = 0;
    while (hidden < buttons.length) {
      const visible = buttons.slice(0, buttons.length - hidden);
      const totalWidth = visible.reduce((sum, btn, index) => {
        return sum + btn.offsetWidth + (index > 0 ? btnGap : 0);
      }, 0);

      if (totalWidth <= available + 1) break;

      hidden += 1;
      buttons[buttons.length - hidden].classList.add('facets-desktop-drawer__filter-btn--overflow');
    }
  }

  bindTriggers() {
    document
      .querySelectorAll(
        '.facets-desktop-drawer-wrap [data-facet-panel-id], facet-filters-drawer [data-facet-panel-id]'
      )
      .forEach((btn) => {
        if (btn.dataset.facetDrawerBound === 'true') return;
        btn.dataset.facetDrawerBound = 'true';
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          const panelId = btn.dataset.facetPanelId;
          const scrollTo = btn.dataset.scrollTo;
          if (panelId === 'all') {
            this.open(null, scrollTo);
          } else {
            this.open(panelId);
          }
        });
      });

    this.querySelectorAll('[data-facets-drawer-back]').forEach((btn) => {
      if (btn.dataset.facetDrawerBackBound === 'true') return;
      btn.dataset.facetDrawerBackBound = 'true';
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.showAll();
        this.updateAllFiltersList();
      });
    });

    this.querySelectorAll('[data-facets-drawer-close]').forEach((el) => {
      if (el.dataset.facetDrawerCloseBound === 'true') return;
      el.dataset.facetDrawerCloseBound = 'true';
      el.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });
    });

    document.querySelectorAll('[data-facets-drawer-open]').forEach((btn) => {
      if (btn.dataset.facetDrawerOpenBound === 'true') return;
      btn.dataset.facetDrawerOpenBound = 'true';
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    });
  }

  isMobileDrawer() {
    return window.innerWidth < 1025;
  }

  bindApplyButtons() {
    const applyAll = this.querySelector('[data-facets-drawer-apply-all]');
    if (applyAll && applyAll.dataset.facetDrawerApplyAllBound !== 'true') {
      applyAll.dataset.facetDrawerApplyAllBound = 'true';
      applyAll.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.isMobileDrawer()) {
          this.querySelector('facet-filters-form')?.applyDrawerFilters?.(event);
          this.close({ revertDraft: false });
          return;
        }
        this.close();
      });
    }

    this.querySelectorAll('[data-facets-drawer-apply-filter]').forEach((btn) => {
      if (btn.dataset.facetDrawerApplyFilterBound === 'true') return;
      btn.dataset.facetDrawerApplyFilterBound = 'true';
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.isMobileDrawer()) {
          this.querySelector('facet-filters-form')?.applyDrawerFilters?.(event);
          this.close({ revertDraft: false });
        } else {
          this.close();
        }
      });
    });
  }

  bindSizeInputSync() {
    const form = this.querySelector('#FacetFiltersForm');
    if (!form || form.dataset.sizeSyncBound === 'true') return;
    form.dataset.sizeSyncBound = 'true';
    form.addEventListener('change', (event) => {
      const input = event.target;
      if (input.type !== 'checkbox' || !input.classList.contains('facets-drawer-size-input')) return;
      const item = input.closest('.facets__item--size');
      const desktopBtn = item?.querySelector('.facet-size-btn');
      desktopBtn?.classList.toggle('facet-size-btn--active', input.checked);
    });
  }

  resetPanelFiltersLocal(panel) {
    if (!panel) return;
    panel.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
      input.checked = false;
    });
    panel.querySelectorAll('.facet-size-btn--active').forEach((btn) => {
      btn.classList.remove('facet-size-btn--active');
    });
  }

  resetAllFiltersLocal() {
    this.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
      input.checked = false;
    });
    this.querySelectorAll('.facet-size-btn--active').forEach((btn) => {
      btn.classList.remove('facet-size-btn--active');
    });
    this.querySelectorAll('facets-drawer-price-range').forEach((price) => {
      price.resetLocal?.();
    });
  }

  updateMobileChrome(mode = 'all', panelId = null) {
    const wrapper = this.querySelector('.facets-desktop-drawer__drawer-header-mobile');
    const headerAll = this.querySelector('[data-drawer-header-all]');
    const headerFilter = this.querySelector('[data-drawer-header-filter]');
    const titleTextEl = this.querySelector('[data-drawer-filter-title-text]');

    if (!wrapper || !headerAll || !headerFilter) return;

    if (mode === 'filter' && panelId) {
      const panel = document.getElementById(panelId);
      if (titleTextEl) titleTextEl.textContent = panel?.dataset.filterLabel || '';
      wrapper.dataset.mobileHeaderView = 'filter';
      headerAll.setAttribute('hidden', '');
      headerFilter.removeAttribute('hidden');
      this.updateFilterHeaderCount(panel);
    } else {
      if (titleTextEl) titleTextEl.textContent = '';
      wrapper.dataset.mobileHeaderView = 'all';
      headerAll.removeAttribute('hidden');
      headerFilter.setAttribute('hidden', '');
      this.updateFilterHeaderCount(null);
    }
  }

  updateFilterHeaderCount(panel = null) {
    const countEl = this.querySelector('[data-drawer-filter-count]');
    if (!countEl) return;

    const activePanel =
      panel instanceof HTMLElement
        ? panel
        : this.querySelector('.facets-desktop-drawer__panel--filter.is-active');

    if (!activePanel) {
      countEl.textContent = '';
      countEl.setAttribute('hidden', '');
      return;
    }

    const count = activePanel.querySelectorAll('input[type="checkbox"]:checked').length;
    if (count > 0) {
      countEl.textContent = String(count);
      countEl.hidden = false;
      countEl.removeAttribute('hidden');
      countEl.classList.add('is-visible');
    } else {
      countEl.textContent = '';
      countEl.hidden = true;
      countEl.setAttribute('hidden', '');
      countEl.classList.remove('is-visible');
    }
  }

  onKeyDown(event) {
    if (event.code.toUpperCase() === 'ESCAPE' && this.classList.contains('active')) {
      if (this.querySelector('.facets-desktop-drawer__panel--filter.is-active')) {
        this.showAll();
      } else {
        this.close();
      }
    }
  }

  onFiltersUpdated() {
    this.allPanel = this.querySelector('.facets-desktop-drawer__panel--all');
    this.filterPanels = this.querySelectorAll('.facets-desktop-drawer__panel--filter');
    this.bindTriggers();
    this.bindApplyButtons();
    this.bindFormChange();
    this.bindSizeInputSync();
    this.captureAppliedFilterState();
    this.updateAllFiltersList();
    this.fitColorLabels();
    this.updateStickyButtonStates();
    requestAnimationFrame(() => {
      this.syncStickyBarBackground();
      this.updateDesktopStickyUnit();
      this.fitFilterButtons();
      this.fitColorLabels();
    });
    const activeFilterPanel = this.querySelector('.facets-desktop-drawer__panel--filter.is-active');
    this.updateMobileChrome(activeFilterPanel ? 'filter' : 'all', activeFilterPanel?.id || null);
    if (activeFilterPanel) this.updateFilterHeaderCount(activeFilterPanel);
  }

  fitColorLabels() {
    const maxSize = 13;
    const minSize = 8;

    this.querySelectorAll('.facet-checkbox--color .facets-drawer-color__label').forEach((label) => {
      label.style.fontSize = `${maxSize}px`;

      let size = maxSize;
      while (label.scrollWidth > label.clientWidth && size > minSize) {
        size -= 0.5;
        label.style.fontSize = `${size}px`;
      }
    });
  }

  onFormChange() {
    requestAnimationFrame(() => {
      this.updateFilterHeaderCount();
      this.updateAllFiltersList();
      this.updateStickyButtonStates();
    });
  }

  bindFormChange() {
    const form = this.querySelector('#FacetFiltersForm');
    if (!form || form.dataset.drawerChangeBound === 'true') return;
    form.dataset.drawerChangeBound = 'true';
    form.addEventListener('change', this.onFormChange);
    form.addEventListener('input', (event) => {
      if (event.target.closest('facets-drawer-price-range')) {
        this.onFormChange();
      }
    });
  }

  getCheckboxLabel(panel, input) {
    let labelEl = input.closest('label');
    if (!labelEl && input.id) {
      labelEl = panel.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    }
    if (!labelEl) {
      const item = input.closest('.facets__item');
      labelEl = item?.querySelector('label[for="' + CSS.escape(input.id) + '"]') || null;
    }
    if (!labelEl) return '';

    const colorLabel = labelEl.querySelector('.facets-drawer-color__label');
    if (colorLabel?.textContent.trim()) return colorLabel.textContent.trim();

    const sizeTile = labelEl.querySelector('.facets-drawer-size__tile-text');
    if (sizeTile?.textContent.trim()) return sizeTile.textContent.trim();

    const sizeText = labelEl.querySelector('.facet-size-btn__text');
    if (sizeText?.textContent.trim()) return sizeText.textContent.trim();

    const checkboxLabel = labelEl.querySelector('.checkbox__label');
    if (checkboxLabel?.textContent.trim()) return checkboxLabel.textContent.trim();

    return '';
  }

  getPanelActiveLabels(panel) {
    const labels = [];
    panel.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
      const text = this.getCheckboxLabel(panel, input);
      if (text) labels.push(text);
    });
    return labels;
  }

  getPanelCheckboxState(panel) {
    return [...panel.querySelectorAll('input[type="checkbox"]')].map((input) => ({
      id: input.id,
      checked: input.checked,
      disabled: input.disabled,
      label: this.getCheckboxLabel(panel, input),
    }));
  }

  getPriceState() {
    const priceSection = this.querySelector('.facets-desktop-drawer__all-section--price');
    if (!priceSection) return { active: false };
    const minInput = priceSection.querySelector('[data-price-input="min"]');
    const maxInput = priceSection.querySelector('[data-price-input="max"]');
    return {
      active: Boolean(
        (minInput?.hasAttribute('name') && minInput?.value?.trim()) ||
          (maxInput?.hasAttribute('name') && maxInput?.value?.trim())
      ),
      minValue: minInput?.value || '',
      maxValue: maxInput?.value || '',
      minHasName: minInput?.hasAttribute('name'),
      maxHasName: maxInput?.hasAttribute('name'),
    };
  }

  captureAppliedFilterState() {
    this._appliedFilterState = {};
    this.filterPanels.forEach((panel) => {
      this._appliedFilterState[panel.id] = this.getPanelCheckboxState(panel);
    });
    this._appliedPriceState = this.getPriceState();
  }

  revertPanelToApplied(panelId) {
    const panel = document.getElementById(panelId);
    const saved = this._appliedFilterState?.[panelId];
    if (!panel || !saved) return;

    saved.forEach((item) => {
      const input = panel.querySelector(`#${CSS.escape(item.id)}`);
      if (!input) return;
      input.checked = item.checked;
      const desktopBtn = panel.querySelector(`label[for="${CSS.escape(item.id)}"].facet-size-btn`);
      desktopBtn?.classList.toggle('facet-size-btn--active', item.checked);
    });
    this.updateFilterHeaderCount();
  }

  revertAllDraftChanges() {
    Object.keys(this._appliedFilterState || {}).forEach((panelId) => {
      this.revertPanelToApplied(panelId);
    });
  }

  getAppliedLabelsForPanel(panelId) {
    const saved = this._appliedFilterState?.[panelId] || [];
    return saved.filter((item) => item.checked).map((item) => item.label).filter(Boolean);
  }

  isPriceFilterActive(appliedOnly = false) {
    if (appliedOnly && this._appliedPriceState) {
      return this._appliedPriceState.active;
    }
    const priceSection = this.querySelector('.facets-desktop-drawer__all-section--price');
    if (!priceSection) return false;
    const minInput = priceSection.querySelector('[data-price-input="min"]');
    const maxInput = priceSection.querySelector('[data-price-input="max"]');
    return Boolean(
      (minInput?.hasAttribute('name') && minInput?.value?.trim()) ||
        (maxInput?.hasAttribute('name') && maxInput?.value?.trim())
    );
  }

  updateAllFiltersList() {
    if (!this.allPanel) return;

    const useAppliedOnly = this.isMobileDrawer();
    let totalActive = this.isPriceFilterActive(useAppliedOnly) ? 1 : 0;

    this.querySelectorAll('.facets-desktop-drawer__all-row').forEach((row) => {
      const panelId = row.dataset.facetPanelId;
      const panel = panelId ? document.getElementById(panelId) : null;
      const labels = useAppliedOnly
        ? this.getAppliedLabelsForPanel(panelId)
        : panel
          ? this.getPanelActiveLabels(panel)
          : [];
      const count = labels.length;

      row.classList.toggle('facets-desktop-drawer__all-row--active', count > 0);

      const main = row.querySelector('.facets-desktop-drawer__all-row-main');
      let summary = main?.querySelector('.facets-desktop-drawer__all-row-summary');

      if (count > 0) {
        if (!summary && main) {
          summary = document.createElement('span');
          summary.className = 'facets-desktop-drawer__all-row-summary';
          main.appendChild(summary);
        }
        if (summary) summary.textContent = labels.join(', ');
        totalActive += count;
      } else if (summary) {
        summary.remove();
      }

      const meta = row.querySelector('.facets-desktop-drawer__all-row-meta');
      let countEl = meta?.querySelector('.facets-desktop-drawer__all-row-count');

      if (count > 0) {
        if (!countEl && meta) {
          countEl = document.createElement('span');
          countEl.className = 'facets-desktop-drawer__all-row-count';
          meta.insertBefore(countEl, meta.firstChild);
        }
        if (countEl) countEl.textContent = String(count);
      } else if (countEl) {
        countEl.remove();
      }
    });

    this.querySelectorAll('.facets-desktop-drawer__all-title').forEach((title) => {
      let totalEl = title.querySelector('.facets-desktop-drawer__all-total');
      if (totalActive > 0) {
        if (!totalEl) {
          totalEl = document.createElement('span');
          totalEl.className = 'facets-desktop-drawer__all-total';
          title.appendChild(totalEl);
        }
        totalEl.textContent = String(totalActive);
      } else if (totalEl) {
        totalEl.remove();
      }
    });
  }

  getStickyTopOffset() {
    const sectionHeader = document.querySelector('.section-header');
    if (sectionHeader) {
      const bottom = sectionHeader.getBoundingClientRect().bottom;
      if (bottom > 0) return Math.ceil(bottom);
    }

    const cssHeader = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    if (cssHeader > 0) return Math.ceil(cssHeader);

    return 0;
  }

  initDesktopStickyUnit() {
    if (window.innerWidth < 1025 || this._stickyInstance) return;

    const stickyScope = document.querySelector('.facets-desktop-drawer-sticky-scope');
    const stickyUnit = stickyScope?.querySelector('.facets-desktop-drawer__sticky-unit');
    if (!stickyScope || !stickyUnit) return;

    const stickySpacer = document.createElement('div');
    stickySpacer.className = 'facets-desktop-drawer__sticky-spacer';
    stickyUnit.before(stickySpacer);

    this._stickyInstance = { stickyScope, stickyUnit, stickySpacer };
    this.syncStickyBarBackground();
    this.updateDesktopStickyUnit();
  }

  syncStickyBarBackground() {
    if (window.innerWidth < 1025) return;

    const headerMain = document.querySelector('.section-header .header__desktop .header__main');
    const stickyUnit = document.querySelector('.facets-desktop-drawer__sticky-unit');
    if (!headerMain || !stickyUnit) return;

    const styles = getComputedStyle(headerMain);
    stickyUnit.style.background = styles.background;
    stickyUnit.style.backdropFilter = styles.backdropFilter;
    stickyUnit.style.webkitBackdropFilter = styles.webkitBackdropFilter;
  }

  teardownDesktopStickyUnit() {
    if (!this._stickyInstance) return;

    const { stickyUnit, stickySpacer } = this._stickyInstance;
    stickyUnit.classList.remove('is-pinned');
    stickyUnit.style.removeProperty('top');
    stickyUnit.style.removeProperty('left');
    stickyUnit.style.removeProperty('width');
    stickyUnit.style.removeProperty('background');
    stickyUnit.style.removeProperty('backdrop-filter');
    stickyUnit.style.removeProperty('-webkit-backdrop-filter');
    stickySpacer.remove();
    this._stickyInstance = null;
  }

  updateDesktopStickyUnit() {
    if (!this._stickyInstance || window.innerWidth < 1025) return;

    const { stickyScope, stickyUnit, stickySpacer } = this._stickyInstance;
    if (!document.body.contains(stickyScope)) {
      this.teardownDesktopStickyUnit();
      this.initDesktopStickyUnit();
      return;
    }

    const topOffset = this.getStickyTopOffset();
    const scopeRect = stickyScope.getBoundingClientRect();
    const unitHeight = stickyUnit.offsetHeight;
    const shouldPin = scopeRect.top < topOffset && scopeRect.bottom > topOffset + unitHeight;

    if (shouldPin) {
      stickyUnit.classList.add('is-pinned');
      stickyUnit.style.top = `${topOffset}px`;
      stickyUnit.style.left = `${scopeRect.left}px`;
      stickyUnit.style.width = `${scopeRect.width}px`;
      stickySpacer.style.height = `${unitHeight}px`;
      this.syncStickyBarBackground();
    } else {
      stickyUnit.classList.remove('is-pinned');
      stickyUnit.style.removeProperty('top');
      stickyUnit.style.removeProperty('left');
      stickyUnit.style.removeProperty('width');
      stickySpacer.style.height = '0';
    }
  }

  updateStickyButtonStates() {
    document.querySelectorAll('.facets-desktop-drawer__filter-btn[data-facet-panel-id]').forEach((btn) => {
      if (btn.dataset.facetPanelId === 'all') return;

      const panel = document.getElementById(btn.dataset.facetPanelId);
      if (!panel) return;

      let activeCount = panel.querySelectorAll('input[type="checkbox"]:checked').length;
      if (btn.dataset.scrollTo === 'price') {
        const priceSection = document.querySelector('.facets-desktop-drawer__all-section--price');
        const minInput = priceSection?.querySelector('[data-price-input="min"]');
        const maxInput = priceSection?.querySelector('[data-price-input="max"]');
        const minActive = Boolean(minInput?.hasAttribute('name') && minInput?.value?.trim());
        const maxActive = Boolean(maxInput?.hasAttribute('name') && maxInput?.value?.trim());
        activeCount = minActive || maxActive ? 1 : 0;
      }

      btn.classList.toggle('facets-desktop-drawer__filter-btn--active', activeCount > 0);

      let countEl = btn.querySelector('.facets-desktop-drawer__filter-btn-count');
      if (activeCount > 0) {
        if (!countEl) {
          countEl = document.createElement('span');
          countEl.className = 'facets-desktop-drawer__filter-btn-count';
          btn.appendChild(countEl);
        }
        countEl.textContent = activeCount;
      } else if (countEl) {
        countEl.remove();
      }
    });
  }

  _onDrawerOpenEnd(event) {
    const content = this.querySelector('.popup-modal__content');
    if (event.target !== content) return;
    if (event.animationName !== 'facets-drawer-enter' && event.animationName !== 'facets-drawer-enter-rtl') {
      return;
    }

    content.removeEventListener('animationend', this._onDrawerOpenEnd);
    if (!this.classList.contains('active')) return;

    this.classList.remove('drawer-opening');
    this.classList.add('animate');
    this.trapDrawerFocus();
  }

  isDrawerOpen() {
    return this.classList.contains('active');
  }

  switchPanel(panelId = null, scrollTo = null) {
    if (panelId && panelId !== 'all') {
      this.showPanel(panelId);
    } else {
      this.showAll(scrollTo);
    }
  }

  open(panelId = null, scrollTo = null) {
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }

    if (this.isDrawerOpen()) {
      this.switchPanel(panelId, scrollTo);
      return;
    }

    const content = this.querySelector('.popup-modal__content');
    content?.removeEventListener('animationend', this._onDrawerOpenEnd);

    this.classList.remove('animate', 'drawer-opening');
    this.captureAppliedFilterState();

    this.switchPanel(panelId, scrollTo);

    this.classList.add('active');
    this.setAttribute('aria-hidden', 'false');

    if (document.querySelectorAll('.popup-modal.active').length <= 1) {
      document.body.classList.add('overflow-hidden');
    }

    if (!content) return;

    void content.offsetWidth;

    const startOpenAnimation = () => {
      if (!this.classList.contains('active')) return;
      this.classList.add('drawer-opening');
      content.addEventListener('animationend', this._onDrawerOpenEnd);

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this._onDrawerOpenEnd({
          target: content,
          animationName: 'facets-drawer-enter',
        });
      }
    };

    requestAnimationFrame(startOpenAnimation);
  }

  trapDrawerFocus() {
    const inner = this.querySelector('.facets-desktop-drawer__inner');
    if (!inner || typeof trapFocus !== 'function') return;

    const headerWrap = this.querySelector('.facets-desktop-drawer__drawer-header-mobile');
    let focusTarget = inner;
    if (headerWrap?.dataset.mobileHeaderView === 'filter') {
      focusTarget = this.querySelector('.facets-desktop-drawer__back--header') || inner;
    } else {
      focusTarget =
        this.querySelector('.facets-desktop-drawer__close-btn--all-view') ||
        this.querySelector('.facets-desktop-drawer__close-btn--desktop-only') ||
        inner;
    }
    trapFocus(inner, focusTarget);
  }

  close({ revertDraft = true } = {}) {
    if (revertDraft && this.isMobileDrawer()) {
      this.revertAllDraftChanges();
    }
    const content = this.querySelector('.popup-modal__content');
    content?.removeEventListener('animationend', this._onDrawerOpenEnd);
    this.classList.remove('drawer-opening', 'animate');
    this._closeTimer = setTimeout(() => {
      this._closeTimer = null;
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      if (document.querySelectorAll('.popup-modal.active').length === 0) {
        document.body.classList.remove('overflow-hidden');
        document.body.style.paddingRight = '';
      }
      this.showAll(null, { revertDraft: false });
    }, 500);

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
  }

  showAll(scrollTo = null, { revertDraft = true } = {}) {
    const activeFilterPanel = this.querySelector('.facets-desktop-drawer__panel--filter.is-active');
    if (revertDraft && this.isMobileDrawer() && activeFilterPanel?.id) {
      this.revertPanelToApplied(activeFilterPanel.id);
    }

    if (this.allPanel) {
      this.allPanel.classList.add('is-active');
      this.allPanel.removeAttribute('hidden');
    }
    this.filterPanels.forEach((panel) => {
      panel.classList.remove('is-active');
      panel.setAttribute('hidden', '');
    });

    this.updateAllFiltersList();
    this.fitColorLabels();

    if (scrollTo === 'price') {
      requestAnimationFrame(() => {
        this.querySelector('.facets-desktop-drawer__all-section--price')?.scrollIntoView({ block: 'start' });
      });
    }

    this.updateMobileChrome('all');
  }

  showPanel(panelId) {
    if (this.allPanel) {
      this.allPanel.classList.remove('is-active');
      this.allPanel.setAttribute('hidden', '');
    }
    this.filterPanels.forEach((panel) => {
      const isTarget = panel.id === panelId;
      panel.classList.toggle('is-active', isTarget);
      if (isTarget) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    requestAnimationFrame(() => this.fitColorLabels());
    this.updateMobileChrome('filter', panelId);
  }
}

customElements.define('facet-filters-drawer', FacetFiltersDrawer);
